import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDownIcon, Briefcase } from 'lucide-react';
import { usePositionsStore } from '@/store/positionsStore';
import type { Position } from '@arrowise/shared';

interface PositionFilterDropdownProps {
    positionId?: string;
    onPositionFilterChange: (positionId?: string) => void;
    disabled?: boolean;
}

export function PositionFilterDropdown({
    positionId,
    onPositionFilterChange,
    disabled = false
}: PositionFilterDropdownProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { positions, isLoading } = usePositionsStore();

    const getPositionFilterLabel = (selectedPositionId?: string) => {
        if (!selectedPositionId) return 'All Positions';
        const position = positions.find(p => p.id === selectedPositionId);
        return position?.name || 'Unknown Position';
    };

    const handlePositionSelect = (selectedPositionId?: string) => {
        onPositionFilterChange(selectedPositionId);
        setDropdownOpen(false);
    };

    const positionOptions: Array<{ value?: string; label: string; position?: Position }> = [
        { value: undefined, label: 'All Positions' },
        ...positions.map(position => ({
            value: position.id,
            label: position.name,
            position
        }))
    ];

    return (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-48 justify-between" disabled={disabled || isLoading}>
                    <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        {getPositionFilterLabel(positionId)}
                    </div>
                    <ChevronDownIcon className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                {positionOptions.map((option) => {
                    const isSelected = option.value === positionId;
                    return (
                        <DropdownMenuItem
                            key={option.value || 'all'}
                            className="hover:cursor-pointer"
                            onClick={() => handlePositionSelect(option.value)}
                            onSelect={(e) => e.preventDefault()}
                        >
                            <div className="flex items-center gap-2 w-full">
                                {option.position && <Briefcase className="h-4 w-4" />}
                                {option.label}
                                {isSelected && <span className="ml-auto text-xs">✓</span>}
                            </div>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
